import { WebStorage, WebStorageNames } from "../../../../utils";

export function formatPeriodDate(d) {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function getCurrentMonthPeriod() {
  const now = new Date();
  return {
    periodFrom: new Date(now.getFullYear(), now.getMonth(), 1),
    periodTo: new Date(now.getFullYear(), now.getMonth() + 1, 0),
  };
}

export function buildPeriodQuery(periodFrom, periodTo) {
  const params = new URLSearchParams();
  if (periodFrom) params.set("periodFrom", formatPeriodDate(periodFrom));
  if (periodTo) params.set("periodTo", formatPeriodDate(periodTo));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function getRoleId() {
  try {
    const fromStore = WebStorage.getLocalStore(WebStorageNames.RoleId);
    if (fromStore != null && fromStore !== "") return parseInt(fromStore, 10);
  } catch {
    /* ignore */
  }
  const raw = localStorage.getItem("roleId");
  return raw != null && raw !== "" ? parseInt(raw, 10) : null;
}

/** @returns {'admin' | 'manager' | 'user'} */
export function getDashboardRoleType() {
  const roleId = getRoleId();
  if (roleId === 3) return "manager";
  if (roleId === 4) return "user";
  if (roleId === 1 || roleId === 2) return "admin";
  return "user";
}

export function startOfDay(d) {
  const date = d instanceof Date ? new Date(d) : new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function validatePeriod(periodFrom, periodTo) {
  const errors = {};
  if (!periodFrom) errors.periodFrom = "required";
  if (!periodTo) errors.periodTo = "required";

  if (periodFrom && periodTo) {
    const from = startOfDay(periodFrom);
    const to = startOfDay(periodTo);
    if (from.getTime() > to.getTime()) {
      errors.periodTo = "endBeforeStart";
    } else {
      const dayMs = 86400000;
      const days = Math.floor((to - from) / dayMs) + 1;
      if (days > 366) errors.range = "maxRange";
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export function getPeriodPresets() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  const lastMonthEnd = new Date(y, m, 0);
  const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);

  return [
    {
      id: "thisMonth",
      getRange: () => getCurrentMonthPeriod(),
    },
    {
      id: "lastMonth",
      getRange: () => ({
        periodFrom: lastMonthStart,
        periodTo: lastMonthEnd,
      }),
    },
    {
      id: "last3Months",
      getRange: () => ({
        periodFrom: new Date(y, m - 2, 1),
        periodTo: new Date(y, m + 1, 0),
      }),
    },
    {
      id: "thisYear",
      getRange: () => ({
        periodFrom: new Date(y, 0, 1),
        periodTo: new Date(y, 11, 31),
      }),
    },
  ];
}

function mapBreakdownItem(item) {
  const rawName = item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? item?.type ?? item?.Type ?? "";
  const id = String(item?.id ?? item?.Id ?? "");
  const name = String(rawName).trim() || (id === "" ? "Unassigned" : "Unknown");
  return {
    id,
    name,
    count: Number(item?.count ?? item?.Count ?? 0),
  };
}

export function getBreakdownChartData(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item, idx) => ({
      id: item.id || `item-${idx}`,
      name: item.name || "—",
      count: Number(item.count) || 0,
    }))
    .filter((item) => item.count > 0);
}

function mapBreakdownList(list) {
  return Array.isArray(list) ? list.map(mapBreakdownItem) : [];
}

export function mapAdminDashboardSummary(d) {
  const genderRatio = d.genderRatio ?? d.GenderRatio ?? {};
  return {
    periodFrom: d.periodFrom ?? d.PeriodFrom ?? null,
    periodTo: d.periodTo ?? d.PeriodTo ?? null,
    totalWorkplaces: Number(d.totalWorkplaces ?? d.TotalWorkplaces ?? 0),
    totalDepartments: Number(d.totalDepartments ?? d.TotalDepartments ?? 0),
    totalUsers: Number(d.totalUsers ?? d.TotalUsers ?? 0),
    totalLeaveTypes: Number(d.totalLeaveTypes ?? d.TotalLeaveTypes ?? 0),
    peopleOnLeave: Number(d.peopleOnLeave ?? d.PeopleOnLeave ?? 0),
    peopleWorked: Number(d.peopleWorked ?? d.PeopleWorked ?? 0),
    totalHoursWorked: Number(d.totalHoursWorked ?? d.TotalHoursWorked ?? 0),
    totalTeams: Number(d.totalTeams ?? d.TotalTeams ?? 0),
    totalProjects: Number(d.totalProjects ?? d.TotalProjects ?? 0),
    totalEmploymentTypes: Number(d.totalEmploymentTypes ?? d.TotalEmploymentTypes ?? 0),
    totalDesignations: Number(d.totalDesignations ?? d.TotalDesignations ?? 0),
    attendanceRatePercent: Number(d.attendanceRatePercent ?? d.AttendanceRatePercent ?? 0),
    totalDocumentsUploaded: Number(d.totalDocumentsUploaded ?? d.TotalDocumentsUploaded ?? 0),
    employmentTypeBreakdown: mapBreakdownList(d.employmentTypeBreakdown ?? d.EmploymentTypeBreakdown),
    genderBreakdown: mapBreakdownList(d.genderBreakdown ?? d.GenderBreakdown),
    genderRatio: {
      male: Number(genderRatio.male ?? genderRatio.Male ?? 0),
      female: Number(genderRatio.female ?? genderRatio.Female ?? 0),
      total: Number(genderRatio.total ?? genderRatio.Total ?? 0),
      malePercentage: Number(genderRatio.malePercentage ?? genderRatio.MalePercentage ?? 0),
      femalePercentage: Number(genderRatio.femalePercentage ?? genderRatio.FemalePercentage ?? 0),
    },
    personsOnLeaveToday: Number(d.personsOnLeaveToday ?? d.PersonsOnLeaveToday ?? 0),
    leaveRequestsToday: Number(d.leaveRequestsToday ?? d.LeaveRequestsToday ?? 0),
    pendingLeaveRequestsToday: Number(d.pendingLeaveRequestsToday ?? d.PendingLeaveRequestsToday ?? 0),
    documentsUploadedToday: Number(d.documentsUploadedToday ?? d.DocumentsUploadedToday ?? 0),
    peoplePresentToday: Number(d.peoplePresentToday ?? d.PeoplePresentToday ?? 0),
    timesheetsSubmittedToday: Number(d.timesheetsSubmittedToday ?? d.TimesheetsSubmittedToday ?? 0),
  };
}

export function mapManagerDashboardSummary(d) {
  return {
    periodFrom: d.periodFrom ?? d.PeriodFrom ?? null,
    periodTo: d.periodTo ?? d.PeriodTo ?? null,
    teamMemberCount: Number(d.teamMemberCount ?? d.TeamMemberCount ?? 0),
    teamMembersPresent: Number(d.teamMembersPresent ?? d.TeamMembersPresent ?? 0),
    attendanceRatePercent: Number(d.attendanceRatePercent ?? d.AttendanceRatePercent ?? 0),
    peopleOnLeave: Number(d.peopleOnLeave ?? d.PeopleOnLeave ?? 0),
    peopleWorked: Number(d.peopleWorked ?? d.PeopleWorked ?? 0),
    totalHoursWorked: Number(d.totalHoursWorked ?? d.TotalHoursWorked ?? 0),
    projectsAssigned: Number(d.projectsAssigned ?? d.ProjectsAssigned ?? 0),
    pendingLeaveRequests: Number(d.pendingLeaveRequests ?? d.PendingLeaveRequests ?? 0),
    employmentTypeBreakdown: mapBreakdownList(d.employmentTypeBreakdown ?? d.EmploymentTypeBreakdown),
  };
}

export function mapUserDashboardSummary(d) {
  return {
    periodFrom: d.periodFrom ?? d.PeriodFrom ?? null,
    periodTo: d.periodTo ?? d.PeriodTo ?? null,
    totalHoursWorked: Number(d.totalHoursWorked ?? d.TotalHoursWorked ?? 0),
    submittedTimeSheets: Number(d.submittedTimeSheets ?? d.SubmittedTimeSheets ?? 0),
    attendanceRatePercent: Number(d.attendanceRatePercent ?? d.AttendanceRatePercent ?? 0),
    documentsUploaded: Number(d.documentsUploaded ?? d.DocumentsUploaded ?? 0),
    leaveDaysInPeriod: Number(d.leaveDaysInPeriod ?? d.LeaveDaysInPeriod ?? 0),
    pendingLeaveRequests: Number(d.pendingLeaveRequests ?? d.PendingLeaveRequests ?? 0),
    isOnLeaveToday: Boolean(d.isOnLeaveToday ?? d.IsOnLeaveToday),
    assignedProjects: Number(d.assignedProjects ?? d.AssignedProjects ?? 0),
    teamCount: Number(d.teamCount ?? d.TeamCount ?? 0),
  };
}

export function formatHours(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "0";
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

export function formatPercent(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "0%";
  return `${n % 1 === 0 ? n : n.toFixed(1)}%`;
}
